import { Injectable } from '@angular/core';
import { LocaleService } from '@next/core-lib/i18n';
import { NotificationActions } from '@next/core-lib/notification';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';

import { debounceTime, map, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import {
  changeCropFieldFilter,
  changeCropFieldsPage,
  changeCustomerFilter,
  changeSearchFilter,
  changeSelectedCropYear,
  changeStatusFilter,
  confirmCancellations,
  confirmCancellationsFailure,
  confirmCancellationsSuccess,
  loadBBACropFieldsFailure,
  loadBBACropFieldsRequest,
  loadBBACropFieldsSuccess,
  resetAllFilters,
  toggleCancellation,
  toggleCancellationFailure,
  toggleCancellationSuccess,
  toggleIsAdministrationInformedOfCancellation,
  toggleIsAdministrationInformedOfCancellationFailure,
  toggleIsAdministrationInformedOfCancellationSuccess,
  toggleIsFertilabInformedOfCancellation,
  toggleIsFertilabInformedOfCancellationFailure,
  toggleIsFertilabInformedOfCancellationSuccess,
} from './bba-cancellation.actions';
import { selectCropFieldsState } from './bba-cancellation.feature';
import { selectCancellableAdviceIdsByCustomerIds, selectCropYear } from './bba-cancellation.selectors';
import { GetBbaCropFieldsResponse, PaginatedResult } from '../models/bba-cancellation-service.model';
import { mapBbaCropFieldResponse } from '../models/bba-cancellation.model';
import { BbaCancellationService } from '../services/bba-cancellation.service';

@Injectable()
export class BBACancellationEffects {
  onFilterChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(changeSelectedCropYear, changeStatusFilter, resetAllFilters),
      map(() => loadBBACropFieldsRequest()),
    ),
  );

  onTextFilterChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(changeSearchFilter, changeCustomerFilter, changeCropFieldFilter),
      debounceTime(500),
      map(() => loadBBACropFieldsRequest()),
    ),
  );

  onPaginationChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(changeCropFieldsPage),
      debounceTime(100),
      map(() => loadBBACropFieldsRequest()),
    ),
  );

  loadBBACropFields$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadBBACropFieldsRequest),
      concatLatestFrom(() => this.store.select(selectCropFieldsState)),
      switchMap(([_, { filter, pagination }]) =>
        this.bbaCancellationService
          .getBbaCropFields({
            cropYear: filter.cropYear,
            statusFilter: filter.statusFilter,
            page: pagination.pageNumber,
            pageSize: pagination.pageSize,
            cropFieldName: filter.cropFieldName,
            customerName: filter.customerName,
            searchTerm: filter.searchTerm,
          })
          .pipe(
            map((response: PaginatedResult<GetBbaCropFieldsResponse>) => ({
              cropFields: response.items.map(mapBbaCropFieldResponse),
              totalCropFieldsCount: response.totalItemCount,
            })),
            map(({ cropFields, totalCropFieldsCount }) => loadBBACropFieldsSuccess({ cropFields, totalCropFieldsCount })),
            catchError(() => of(loadBBACropFieldsFailure({ translationKey: 'bba-cancellation.errors.generic-load' }))),
          ),
      ),
    ),
  );

  toggleCancellation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(toggleCancellation),
      concatLatestFrom(() => this.store.select(selectCropFieldsState)),
      switchMap(([{ adviceId, customerId, setCancelled }, { filter }]) =>
        this.bbaCancellationService.toggleCancellation(filter.cropYear, adviceId, customerId, setCancelled).pipe(
          map(() => toggleCancellationSuccess({ adviceId, customerId, setCancelled })),
          catchError((_e) =>
            of(
              toggleCancellationFailure({
                translationKey: 'bba-cancellation.errors.generic-toggle-cancellation',
                adviceId,
                customerId,
                setCancelled: !setCancelled,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  confirmCancellations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(confirmCancellations),
      concatLatestFrom(() => [this.store.select(selectCancellableAdviceIdsByCustomerIds), this.store.select(selectCropYear)]),
      switchMap(([_, cancellableAdviceIdsByCustomerIds, cropYear]) =>
        this.bbaCancellationService.sendCancellationMail(cropYear, cancellableAdviceIdsByCustomerIds).pipe(
          map(() => {
            this.store.dispatch(loadBBACropFieldsRequest());
            return confirmCancellationsSuccess();
          }),
          catchError(() => of(confirmCancellationsFailure({ translationKey: 'bba-cancellation.errors.generic-cancellation-confirmation' }))),
        ),
      ),
    ),
  );

  toggleIsFertilabInformedOfCancellation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(toggleIsFertilabInformedOfCancellation),
      concatLatestFrom(() => this.store.select(selectCropYear)),
      switchMap(([{ isFertilabInformed, adviceId, customerId }, cropYear]) =>
        this.bbaCancellationService.toggleIsFertilabInformedOfCancellation(cropYear, adviceId, customerId, isFertilabInformed).pipe(
          map(() => toggleIsFertilabInformedOfCancellationSuccess({ isFertilabInformed, adviceId, customerId })),
          catchError(() =>
            of(
              toggleIsFertilabInformedOfCancellationFailure({
                translationKey: 'bba-cancellation.errors.generic-fertilab-toggle',
                adviceId,
                customerId,
                isFertilabInformed: !isFertilabInformed,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  toggleIsAdministrationInformedOfCancellation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(toggleIsAdministrationInformedOfCancellation),
      concatLatestFrom(() => this.store.select(selectCropYear)),
      switchMap(([{ isAdministrationInformed, adviceId, customerId }, cropYear]) =>
        this.bbaCancellationService.toggleIsAdministrationInformedOfCancellation(cropYear, adviceId, customerId, isAdministrationInformed).pipe(
          map(() => toggleIsAdministrationInformedOfCancellationSuccess({ isAdministrationInformed, adviceId, customerId })),
          catchError(() =>
            of(
              toggleIsAdministrationInformedOfCancellationFailure({
                translationKey: 'bba-cancellation.errors.generic-administration-toggle',
                adviceId,
                customerId,
                isAdministrationInformed: !isAdministrationInformed,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  onError$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        loadBBACropFieldsFailure,
        toggleCancellationFailure,
        confirmCancellationsFailure,
        toggleIsFertilabInformedOfCancellationFailure,
        toggleIsAdministrationInformedOfCancellationFailure,
      ),
      map(({ translationKey }) =>
        NotificationActions.errorSnackbar({
          message: this.localeService.translate(translationKey),
        }),
      ),
    ),
  );

  constructor(
    private readonly actions$: Actions,
    private readonly bbaCancellationService: BbaCancellationService,
    private readonly localeService: LocaleService,
    private readonly store: Store,
  ) {}
}
